import { configuredFetchBaseQuery } from '@config/rtkQuery';
import {
  toastFulfilledPromise,
  toastPendingPromise,
  toastRejectedPromise,
} from '@features/promiseHandlers';
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

export const didGroupsApi = createApi({
  reducerPath: 'didGroupsApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getList: builder.query({
      query: (arg) => {
        const url = 'customer/capacity/did-groups';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'DidGroups', type: login }],
    }),
    getForSelect: builder.query({
      query: () => ({ url: 'customer/capacity/did-groups/for-select' }),
      providesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'DidGroupsForSelect', type: login }],
    }),
    getPlansForSelect: builder.query({
      query: ({ id }) => ({
        url: `customer/capacity/did-groups/countries/${id}/plans/for-select`,
      }),
      providesTags: (result, error, { login, id }) =>
        error ? [] : [{ tag: 'DidGroupsPlansForSelect', type: login, id }],
    }),
    getDetails: builder.query({
      query: ({ id }) => ({ url: `customer/capacity/did-groups/${id}` }),
      providesTags: (result, error, { id, login }) => [
        { tag: `DidGroup`, type: login, id },
      ],
    }),
    updateDidGroup: builder.mutation({
      query: ({ id, data: body }) => ({
        url: `customer/capacity/did-groups/${id}/save`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id, login }) => [
        { tag: 'DidGroups', type: login },
        { tag: 'DidGroupsForSelect', type: login },
        { tag: `DidGroup`, type: login, id },
      ],
    }),
    checkUniqueName: builder.query({
      query: ({ name }) => ({
        url: `customer/capacity/did-groups/check-unique`,
        params: { name },
      }),
    }),
    createDidGroup: builder.mutation({
      query: ({ data }) => {
        const body = {
          name: data.name,
          plan: { pk: data.plan, is_pstn: data.type === 'pstn' },
          country: data.country,
        };
        return {
          url: `customer/capacity/did-groups`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (result, error, { login }) => [
        { tag: 'DidGroups', type: login },
      ],
    }),
    deleteDidGroup: builder.mutation({
      query: ({ id }) => ({
        url: `customer/capacity/did-groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id, login }) => [
        { tag: 'DidGroups', type: login },
        { tag: 'DidGroupsForSelect', type: login },
        { tag: 'DidGroup', type: login, id },
      ],
    }),
  }),
});

const initialState = {
  toast: {},
  isNameUnique: false,
};

const didGroupsSlice = createSlice({
  name: 'didGroups',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addMatcher(
        didGroupsApi.endpoints.createDidGroup.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Saving new DID group...')
      )
      .addMatcher(
        didGroupsApi.endpoints.createDidGroup.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, { render: 'DID Group created.' })
      )
      .addMatcher(
        didGroupsApi.endpoints.createDidGroup.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(
        didGroupsApi.endpoints.updateDidGroup.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Saving Did Group...')
      )
      .addMatcher(
        didGroupsApi.endpoints.updateDidGroup.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, {
            render: 'DID Group is saved.',
          })
      )
      .addMatcher(
        didGroupsApi.endpoints.updateDidGroup.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(
        didGroupsApi.endpoints.deleteDidGroup.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Deleting DID Group...')
      )
      .addMatcher(
        didGroupsApi.endpoints.deleteDidGroup.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, { render: 'DID Group deleted.' })
      )
      .addMatcher(
        didGroupsApi.endpoints.deleteDidGroup.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(
        didGroupsApi.endpoints.checkUniqueName.matchFulfilled,
        (state, action) => {
          state.isNameUnique = action.payload.unique;
        }
      ),
});

export default didGroupsSlice.reducer;
